import type { CSSProperties } from 'react'

import './figures.css'

/**
 * 모듈 내부 3축 로봇 — 와이어프레임 `gantryHTML()` 1365행.
 *
 * 🔴 **염색기 3축은 그리퍼가 아니라 피펫 캐리어다**(§16-1 · MIF-LH-11). 셋을 같게 그리지 마라.
 *    `kind='pipette'` 이면 그리퍼 손가락 대신 분주 헤드를 그린다.
 *
 * 수치(X/Y/Z · 그리퍼)는 이 그림에 쓰지 않는다 — 오른쪽 사이드 패널이 말한다(2026-09-16 지시).
 *
 * 🔴 **이름표는 레일 상자 밖으로 나가지 않는다**(2026-09-21 고침). 캐리어는 레일 위치에
 *    «가운데 맞춤»이라 파킹(0%)에서 이름표 왼쪽 절반(실측 19px)이 상자 밖으로 나갔다.
 *    그 자리에서 두 가지가 났다 — 강조 겹(`.fig-now`)의 테두리가 글자를 가로질렀고,
 *    창이 좁으면 `.figbody__focus`(`overflow:auto`)가 그 19px 을 **잘라 먹었다**
 *    (왼쪽 넘침은 `scrollLeft` 로 닿을 수 없다 · 실측 967px 폭에서 「3축 로봇」이 「축 로봇」).
 *    ⇒ 아래 `--gantry-pos` 를 `figures.css` 가 받아 **필요한 만큼만** 이름표를 안으로 민다.
 *    🔴 미는 것은 `transform` 이라 **칠하는 자리만** 바뀐다 — 선·정거장·캐리어는 그대로다.
 *    🔴 가운데에서는 **밀림이 0 이다**(`max()`/`min()` 이 0 으로 떨어진다) — 종전과 한 픽셀도 같다.
 */
export type GantryKind = 'grip' | 'pipette'

export interface GantryFigureProps {
  /** 레일 위 위치 0~1. 서버 좌표를 호출부가 비율로 바꿔 넘긴다 */
  position: number
  /** 정거장 수(2 또는 3) */
  stops?: 2 | 3
  kind?: GantryKind
  /** 그리퍼가 뭔가 쥐고 있는가 */
  holding?: boolean
  /** 캐리어 아래 이름표(「3축 로봇」 · 「피펫 3축」) */
  name: string
  label: string
}

export function GantryFigure({
  position,
  stops = 3,
  kind = 'grip',
  holding = false,
  name,
  label,
}: GantryFigureProps) {
  const clamped = Math.max(0, Math.min(1, position))
  /** 레일 위 위치를 백분율 «수»로. `left` 와 `--gantry-pos` 가 같은 값을 봐야 한다 */
  const position100 = Number((clamped * 100).toFixed(1))
  const stopPercents = stops === 2 ? [25, 75] : [15, 50, 85]

  return (
    <div aria-label={label} className="fig fig-gantry" role="img">
      <div className="fig-gantry__line" />
      {stopPercents.map((percent) => (
        <span className="fig-gantry__stop" key={percent} style={{ left: `${String(percent)}%` }} />
      ))}
      {/*
        `--gantry-pos` 는 «단위 없는 0~100» 이다. CSS 가 `calc(var(--gantry-pos) * 1cqw)` 로
        레일 폭에 대한 길이로 바꿔 쓴다 — 그래야 이름표 자기 폭(`50%`)과 견줄 수 있다.
        ⚠ `left` 와 **같은 수**여야 한다. 두 벌로 적지 말고 이 상수 하나에서 낸다.
      */}
      <div
        className="fig-gantry__car"
        style={{ left: `${position100}%`, '--gantry-pos': position100 } as CSSProperties}
      >
        <svg
          className="fig-gantry__grip"
          fill="none"
          height={40}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={3}
          viewBox="0 0 40 40"
          width={40}
        >
          {kind === 'pipette' ? (
            <>
              <rect className="fig-gantry__grip--fill" height={10} rx={2} width={16} x={12} y={4} />
              <rect height={12} rx={2} width={10} x={15} y={14} />
              <path d="M20 26v5l-2 6h4l-2-6" />
            </>
          ) : (
            <>
              <rect className="fig-gantry__grip--fill" height={10} rx={2} width={16} x={12} y={4} />
              <line x1={20} x2={20} y1={14} y2={20} />
              <path
                d={
                  holding
                    ? 'M14 20h12M15 20v9l4 4M25 20v9l-4 4'
                    : 'M11 20h18M12 20v10l3 5M28 20v10l-3 5'
                }
              />
            </>
          )}
        </svg>
        <span className="figlbl">{name}</span>
      </div>
    </div>
  )
}

/**
 * 3축 로봇 포커스 뷰 — `gantryFocusSVG()` 1637행. 레일 · 정거장 3 · Z 드롭 · 그리퍼만 그린다.
 * 정거장 이름·좌표·그리퍼 문구는 오른쪽 패널 몫이다(2026-09-16 지시).
 */
export function GantryFocusFigure({
  position,
  drop,
  holding = false,
  label,
}: {
  /** 레일 위 위치 0~1 */
  position: number
  /** Z 하강 비율 0~1 */
  drop: number
  holding?: boolean
  label: string
}) {
  const width = 640
  const x = 60 + Math.max(0, Math.min(1, position)) * 520
  const z = Math.max(0, Math.min(1, drop)) * 108

  return (
    <svg
      aria-label={label}
      className="fig fig-arm"
      height={260}
      role="img"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.5}
      viewBox={`0 0 ${String(width)} 260`}
      width={width}
    >
      <line strokeWidth={4} x1={30} x2={width - 30} y1={60} y2={60} />
      {[60, 320, 580].map((sx) => (
        <circle className="fig-arm__joint" cx={sx} cy={60} key={sx} r={8} />
      ))}
      <g transform={`translate(${x.toFixed(1)},60)`}>
        <rect className="fig-arm__solid" height={28} rx={6} width={44} x={-22} y={-14} />
        <line strokeDasharray="4 4" x1={0} x2={0} y1={14} y2={60 + z} />
        <g transform={`translate(0,${String(60 + z)})`}>
          <rect className="fig-arm__solid" height={18} rx={4} width={32} x={-16} y={0} />
          <path
            d={holding ? 'M-10 18v22l6 8M10 18v22l-6 8' : 'M-18 18v24l8 10M18 18v24l-8 10'}
            strokeWidth={3}
          />
        </g>
      </g>
    </svg>
  )
}
